// import React, { useEffect } from "react";

// declare global {
//   interface Window {
//     startAnymindTS: () => void;
//     anymindTS: {
//       dispose: () => void;
//     };
//     gtag: (
//       event: string,
//       page_view: string,
//       parameters: { [key: string]: any }
//     ) => void;
//   }
// }
// const AdPage: React.FC = () => {
//   useEffect(() => {
//     // 初始化 Google Analytics 页面视图
//     if (window.gtag) {
//       window.gtag("event", "page_view", {
//         page_path: window.location.pathname,
//         page_title: document.title,
//       });
//       console.log("GA4 Page View Logged!");
//     }

//     // 在组件挂载后启动 ATS
//     try {
//       if (window.startAnymindTS) {
//         window.startAnymindTS();
//         console.log("ATS Initialized!");
//       }
//     } catch (e) {
//       console.error("Error initializing ATS:", e);
//     }

//     // 在组件卸载时清理 ATS
//     return () => {
//       if (window.anymindTS && window.anymindTS.dispose) {
//         window.anymindTS.dispose();
//         console.log("ATS Disposed!");
//       }
//     };
//   }, []); // 空依赖数组确保只在挂载和卸载时运行

//   return (
//     <div className="flex flex-col items-center">
//       {/* 广告容器，ATS 会将广告注入到此元素中 */}
//       <div
//         id="ad_sample"
//         className="flex justify-center items-center w-full py-0 bg-transparent"
//       >
//         {/* Advertisement Container */}
//       </div>
//     </div>
//   );
// };

// export default AdPage;

import React, { useEffect, useRef } from "react";

declare global {
  interface Window {
    startAnymindTS: () => void;
    anymindTS: {
      dispose: () => void;
    };
    gtag: (
      event: string,
      page_view: string,
      parameters: { [key: string]: any }
    ) => void;
  }
}

// Module-level flag to prevent multiple initializations across all AdPage instances
let isAnymindTSInitialized = false;

// Helper function to wait for a function to be available on window
const waitForFunction = (
  functionName: string,
  maxWaitTime: number = 5000,
  checkInterval: number = 100
): Promise<boolean> => {
  return new Promise((resolve) => {
    const startTime = Date.now();
    const checkFunction = () => {
      if (
        (window as any)[functionName] &&
        typeof (window as any)[functionName] === "function"
      ) {
        resolve(true);
        return;
      }
      if (Date.now() - startTime >= maxWaitTime) {
        console.warn(
          `Function ${functionName} not available after ${maxWaitTime}ms`
        );
        resolve(false);
        return;
      }
      setTimeout(checkFunction, checkInterval);
    };
    checkFunction();
  });
};

// Helper function to wait for DOM element to exist
const waitForElement = (
  elementId: string,
  maxWaitTime: number = 3000,
  checkInterval: number = 50
): Promise<HTMLElement | null> => {
  return new Promise((resolve) => {
    const startTime = Date.now();
    const checkElement = () => {
      const element = document.getElementById(elementId);
      if (element) {
        resolve(element);
        return;
      }
      if (Date.now() - startTime >= maxWaitTime) {
        console.warn(`Element #${elementId} not found after ${maxWaitTime}ms`);
        resolve(null);
        return;
      }
      setTimeout(checkElement, checkInterval);
    };
    checkElement();
  });
};

const AdPage: React.FC = () => {
  const hasInitialized = useRef(false);
  const initTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const adContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // GA4 Page view - wait for gtag to be available
    const initGA4 = async () => {
      try {
        const gtagAvailable = await waitForFunction("gtag", 3000);
        if (gtagAvailable && window.gtag && typeof window.gtag === "function") {
          window.gtag("event", "page_view", {
            page_path: window.location.pathname,
            page_title: document.title,
          });
          console.log("GA4 Page View Logged!");
        } else {
          console.warn("gtag function not available");
        }
      } catch (e) {
        console.error("Error logging GA4 page view:", e);
      }
    };

    // Initialize ATS - wait for startAnymindTS and DOM element to be available
    const initATS = async () => {
      try {
        // First, ensure the ad container element exists in DOM
        const adElement = await waitForElement("ad_sample", 2000);
        if (!adElement) {
          console.warn(
            "Ad container element not found, skipping ATS initialization"
          );
          return;
        }

        // Wait for the ATS script to load (max 5 seconds)
        const atsAvailable = await waitForFunction("startAnymindTS", 5000);

        if (
          !isAnymindTSInitialized &&
          !hasInitialized.current &&
          atsAvailable &&
          window.startAnymindTS &&
          typeof window.startAnymindTS === "function"
        ) {
          // Wrap ATS initialization in try-catch to prevent errors from breaking the app
          try {
            window.startAnymindTS();
            isAnymindTSInitialized = true;
            hasInitialized.current = true;
            console.log("ATS Initialized successfully!");
          } catch (atsError) {
            console.error("Error calling startAnymindTS:", atsError);
            // Don't mark as initialized if it failed
            if (atsError instanceof Error) {
              console.error("ATS Error details:", {
                message: atsError.message,
                stack: atsError.stack,
                name: atsError.name,
              });
            }
          }
        } else if (!atsAvailable && !isAnymindTSInitialized) {
          console.warn(
            "startAnymindTS function not available - ATS script may not have loaded"
          );
        }
      } catch (e) {
        console.error("Error in ATS initialization process:", e);
        // Log more details about the error
        if (e instanceof Error) {
          console.error("Error details:", {
            message: e.message,
            stack: e.stack,
            name: e.name,
          });
        }
      }
    };

    // Initialize both with a delay to ensure DOM is ready and scripts are loaded
    initTimeoutRef.current = setTimeout(() => {
      initGA4();
      initATS();
    }, 200);

    // ⚠️ 不要在 unmount 時 dispose（會讓 GPT Ads 崩）
    return () => {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }
      console.log("AdPage unmounted, ATS kept alive.");
    };
  }, []);

  return (
    <div className="flex flex-col items-center">
      <div
        ref={adContainerRef}
        id="ad_sample"
        className="flex justify-center items-center w-full py-0 bg-transparent"
      />
    </div>
  );
};

export default AdPage;
